import { useCallback, useEffect, useState } from 'react';
import { PairingScreen } from './screens/PairingScreen';
import { PlayerScreen } from './screens/PlayerScreen';
import { LoadingScreen } from './screens/LoadingScreen';
import { prefs, recoverCredentialsIfNeeded } from './db/prefs';
import { persistAuth } from './db/persistAuth';
import { contentRepo } from './db/contentRepo';
import { runSync, type SyncProgress } from './sync/downloader';
import { provisionDevice } from './api/provision';
import { getProvisionKey } from './helpers/provisionKey';

type Stage = 'loading' | 'pairing' | 'preparing' | 'player';

export function App() {
  const [stage, setStage] = useState<Stage>('loading');
  const [progress, setProgress] = useState<SyncProgress | null>(null);

  // Run the initial content sync behind a progress screen, then enter the
  // player. Used after pairing/login and on first boot when there's no content.
  const prepareAndPlay = useCallback(async () => {
    setProgress(null);
    setStage('preparing');
    await runSync((p) => setProgress(p));
    setStage('player');
  }, []);

  const boot = useCallback(async () => {
    // Recover from an IndexedDB loss (e.g. corrupted after an unclean power-off)
    // using the localStorage credential backup, so we re-sync instead of forcing
    // a re-pair.
    await recoverCredentialsIfNeeded();

    let token = await prefs.getToken();

    // Still no token (e.g. total storage wipe): try zero-touch provisioning with
    // the key baked into the device image before falling back to pairing.
    if (!token) {
      const key = getProvisionKey();
      if (key && navigator.onLine) {
        const data = await provisionDevice(key);
        if (data?.token) {
          await persistAuth(data.token, data.device.uuid, data.device.id, data.device.config ?? null);
          token = data.token;
        }
      }
    }

    if (!token) {
      setStage('pairing');
      return;
    }

    // Paired already. If we have no content yet, do an initial sync (with a
    // progress screen) before showing the player so we don't flash an empty
    // screen.
    const count = await contentRepo.count();
    if (count === 0 && navigator.onLine) {
      await prepareAndPlay();
    } else {
      setStage('player');
    }
  }, [prepareAndPlay]);

  useEffect(() => {
    void boot();
  }, [boot]);

  const onPaired = useCallback(async () => {
    if (navigator.onLine) {
      await prepareAndPlay();
    } else {
      setStage('player');
    }
  }, [prepareAndPlay]);

  if (stage === 'loading') {
    return (
      <div className="pairing-root" style={{ backgroundColor: 'var(--vz-bg-deep)' }}>
        <div className="pairing-center">
          <div className="pairing-spinner" />
        </div>
      </div>
    );
  }

  if (stage === 'pairing') {
    return <PairingScreen onPaired={onPaired} />;
  }

  if (stage === 'preparing') {
    return <LoadingScreen progress={progress} />;
  }

  return <PlayerScreen />;
}
